import { AlternativeReadModel, CriterionReadModel, CriterionWeightReadModel } from "@Evaluation/ValueAnalysis/Domain/readmodels";
import { KendoChart, KendoChartDataSerie, KendoChartOptions } from "@Kendo/Components/KendoChart";


export class SensitivityAnalysisChartDialogViewModel
{
    opened: boolean = false;
    chart: KendoChart = null;
    goalId: number | null = null;
    criterionId: number | null = null;

    selectedAlternatives: AlternativeReadModel[] = [];
    criteria: CriterionReadModel[] = [];
    weights: CriterionWeightReadModel[] = [];
    get selectedCriterion(): CriterionReadModel { return this.criteria.find(a => a.Id === this.criterionId); }
    get goalSelected(): boolean { return this.criterionId === this.goalId; }


    show(selectedAlternatives: AlternativeReadModel[], criteria: CriterionReadModel[], weights: CriterionWeightReadModel[])
    {
        this.selectedAlternatives = selectedAlternatives;
        this.criteria = criteria;
        this.weights = weights;

        this.goalId = this.criteria.find(c => c.ParentId == null).Id;
        this.criterionId = this.goalId;

        const chartOptions = new KendoChartOptions();
        chartOptions.SetScatterLineSeriesType();
        chartOptions.SetXAxis(0.1, 0, 1, "Criterion weight [%]", "p0", null, true, true, "p1");
        chartOptions.SetYAxis(10, 0, 100, "Score", null, true, true, "n");
        chartOptions.SetTooltip(true, "<div style='padding: 5px;'>#= series.name #</div>");
        chartOptions.SetLegend(true, "top", 15);

        this.chart = KendoChart.CreateChart(chartOptions);
        this.setChartData();

        this.opened = true;
    }

    close()
    {
        this.opened = false;

        this.chart = null;
    }


    renderChart()
    {
        this.chart.Render1("SensitivityAnalysisChart");
    }

    setChartData()
    {
        if (this.goalSelected === true)
        {
            this.chart.SetDataSeries([]);
            this.chart.UpdateOptions();
            return;
        }

        const criterionId = this.selectedCriterion.Id;
        const weights = this.weights;
        const criteria = this.criteria;

        const weightsScenario = SensitivityAnalysis.CalculateWeightsScenario(criterionId, weights, criteria);

        const localWeight1 = weights.find(w => w.CriterionId === criterionId).LocalWeight;
        const localWeight2 = weightsScenario.find(w => w.CriterionId === criterionId).LocalWeight;
        const criteriaIds = criteria.map(c => c.Id);

        const series = this.selectedAlternatives.map(alternative =>
        {
            const score1 = 100 * SensitivityAnalysis.CalculateAlternativeValue(alternative, criteriaIds, weights);
            const score2 = 100 * SensitivityAnalysis.CalculateAlternativeValue(alternative, criteriaIds, weightsScenario);

            const x1 = localWeight1;
            const y1 = score1;
            const x2 = localWeight2;
            const y2 = score2;

            const scoreCurrent = y1;
            const scoreFor0 = SensitivityAnalysis.CalculateLinearEquation(0, x1, y1, x2, y2);
            const scoreFor1 = SensitivityAnalysis.CalculateLinearEquation(1, x1, y1, x2, y2);

            const data = [[0, scoreFor0], [localWeight1, scoreCurrent], [1, scoreFor1]];

            const serie = new KendoChartDataSerie();
            serie.SetName(alternative.Name);
            serie.SetData(data);
            return serie;
        });

        const currentWeightSerie = new KendoChartDataSerie()
        currentWeightSerie.SetName("Current weight");
        currentWeightSerie.SetData([[localWeight1, 0], [localWeight1, 100]]);
        series.push(currentWeightSerie); // add vertical line for current weight

        this.chart.SetDataSeries(series);
        this.chart.UpdateOptions();
    }
}

class SensitivityAnalysis
{
    static CalculateWeightsScenario(criterionId: number, weights: CriterionWeightReadModel[], criteria: CriterionReadModel[]): CriterionWeightReadModel[]
    {
        const weight = weights.find(w => w.CriterionId === criterionId);

        const scenarioLocalWeight = weight.LocalWeight === 0.5 ? 0.1 : 0.5;

        const scenarioWeights = weights.map(w =>
        {
            const cloned = new CriterionWeightReadModel();
            cloned.CriterionId = w.CriterionId;
            cloned.LocalWeight = w.LocalWeight;
            return cloned;
        });

        const criterion = criteria.find(c => c.Id === criterionId);
        const lvlCriteriaIds = criteria.filter(o => o.ParentId === criterion.ParentId).map(a => a.Id);

        const scenarionWeight = scenarioWeights.find(w => w.CriterionId === criterionId);
        scenarionWeight.LocalWeight = scenarioLocalWeight * (1 - weight.LocalWeight) / (1 - scenarioLocalWeight);

        const sum = scenarioWeights
            .filter(w => lvlCriteriaIds.indexOf(w.CriterionId) !== -1)
            .map(a => a.LocalWeight)
            .reduce((sum, curr) => sum + curr, 0);

        scenarioWeights
            .filter(w => lvlCriteriaIds.indexOf(w.CriterionId) !== -1)
            .forEach(w => w.LocalWeight = w.LocalWeight / sum);

        criteria.forEach(c =>
        {
            const w = scenarioWeights.find(w => w.CriterionId === c.Id);
            w.GlobalWeight = SensitivityAnalysis.CalculateGlobalWeight(c, criteria, scenarioWeights);
        });

        return scenarioWeights;
    }

    static CalculateGlobalWeight(criterion: CriterionReadModel, criteria: CriterionReadModel[], weights: CriterionWeightReadModel[]): number
    {
        if (criterion.ParentId == null)
        {
            return 1;
        }

        const criterionWeight = weights.find(w => w.CriterionId === criterion.Id);
        const parent = criteria.find(c => c.Id === criterion.ParentId);
        const parentWeight = weights.find(w => w.CriterionId == parent.Id);

        if (parentWeight.GlobalWeight == null)
        {
            return criterionWeight.LocalWeight * SensitivityAnalysis.CalculateGlobalWeight(parent, criteria, weights);
        }
        else
        {
            return criterionWeight.LocalWeight * parentWeight.GlobalWeight;
        }
    }

    static CalculateLinearEquation(x: number, x1: number, y1: number, x2: number, y2: number): number
    {
        return (y2 - y1) / (x2 - x1) * (x - x1) + y1;
    }

    static CalculateAlternativeValue(alternative: AlternativeReadModel, criteriaIds: number[], allWeights: CriterionWeightReadModel[]): number
    {
        let value = 0;

        criteriaIds.forEach(cId =>
        {
            const score = alternative.Criteria.find(s => s.CriterionId === cId && s.Score != null);

            if (score != null)
            {
                const weight = allWeights.find(w => w.CriterionId === cId);

                if (weight != null)
                {
                    value += score.Score * weight.GlobalWeight;
                }
            }
        });

        return value;
    }
}